import { StyledInput, StyledInputProps } from '@ui';
import { debounce } from 'lodash';
import { ChangeEventHandler, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FilterContext from './FilterContext';

interface Props {
    searchInputProps?: Partial<StyledInputProps>;
}

export const FilterComponent = ({ searchInputProps }: Props) => {
    const { t } = useTranslation('datatable');
    const [filter, setFilter] = useContext(FilterContext);
    const [value, setValue] = useState(filter.full_text ?? '');
    const filterRef = useRef(filter);

    useEffect(() => {
        filterRef.current = filter;
        if (filter.full_text !== value) {
            setValue(filter.full_text ?? '');
        }
    }, [filter]); // reset value if filter changes but not if value changes

    const debouncedSetFilter = useMemo(
        () =>
            debounce((nextValue: string) => {
                const current = filterRef.current;
                setFilter({ ...current, full_text: nextValue === '' ? undefined : nextValue });
            }, 350),
        [setFilter]
    );

    useEffect(() => {
        return () => {
            debouncedSetFilter.cancel();
        };
    }, [debouncedSetFilter]);

    const handleFullTextSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
        setValue(e.target.value);
        debouncedSetFilter(e.target.value);
    };

    return (
        <StyledInput
            flush
            aria-label={t('search.fullText')}
            onChange={handleFullTextSearch}
            value={value}
            fullWidth
            size="sm"
            bordered
            className="tw-my-0"
            labelLeft={t('search.fullText')}
            color="success"
            {...searchInputProps}
        />
    );
};
